import type { BaseCabItemOption, BookingStatus, RideDetails } from '@/api';
import { getBookingAttributes } from '@/features/booking/tracker';
import { createTracker } from '@/lib/analytics/create-tracker';
import type { RescheduleCabOption } from '../-api/reschedule';
import { getTripBookingState } from '../-store';

function getCommonAttributes() {
	const bookingData = getTripBookingState().bookingData;
	if (!bookingData) {
		return {};
	}
	const {
		rideDetails,
		cabDetail,
		bookingDetail: { coupon },
	} = bookingData;
	return getBookingAttributes({
		rideDetails,
		coupon,
		selection: cabDetail,
	});
}

function getBookingPaymentType(): 'Partial' | 'Full' | undefined {
	const cabPricing = getTripBookingState().bookingData?.cabDetail?.pricing;
	if (!cabPricing) return undefined;
	return cabPricing.initialPayAmount !== undefined ? 'Partial' : 'Full';
}

function getTripType(rideDetails: RideDetails) {
	// The analytics type should follow the pickup leg for reschedule events.
	return rideDetails.pickup.type === 'airport' ? 'airport' : 'outstation';
}

function formatDateTime(dateTime: string) {
	const date = new Date(dateTime);

	return `${date.toLocaleDateString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	})} ${date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})}`;
}

function withPickupTime(
	rideDetails: RideDetails,
	pickupTime: string,
): RideDetails {
	return {
		...rideDetails,
		pickup: {
			...rideDetails.pickup,
			dateTime: {
				...rideDetails.pickup.dateTime,
				time: pickupTime,
			},
		},
	};
}

function getReschedulePaymentAmount(
	selectedCab: RescheduleCabOption,
	burnOpted?: boolean,
) {
	const {
		rescheduleDetails: { differentialAmount, status },
		pricing: { burn },
	} = selectedCab;

	if (status !== 'PAYMENT_REQUIRED') {
		return differentialAmount > 0
			? -1 * differentialAmount
			: differentialAmount;
	}
	return burnOpted ? differentialAmount - burn : differentialAmount;
}

function getRescheduleEventAttributes({
	selectedDateTime,
	selectedCab,
	burnOpted,
	includeRequestTimestamp = true,
	includeFunnel = true,
}: {
	selectedDateTime?: string;
	selectedCab?: BaseCabItemOption | RescheduleCabOption;
	burnOpted?: boolean;
	includeRequestTimestamp?: boolean;
	includeFunnel?: boolean;
} = {}) {
	const bookingData = getTripBookingState().bookingData;
	if (!bookingData) {
		return {};
	}

	const {
		parentBookingId,
		rideDetails,
		cabDetail,
		bookingDetail: { bookingId, coupon },
	} = bookingData;
	const effectiveDateTime =
		selectedDateTime || rideDetails.pickup.dateTime.time;
	const effectiveCab = selectedCab || cabDetail;
	const paymentType = getBookingPaymentType();

	// Provider and partner identifiers are not exposed in the current booking DTOs.
	// Add them here once the backend surfaces the fields in trip/reschedule payloads.
	return {
		'Booking ID': bookingId,
		...getBookingAttributes({
			rideDetails: withPickupTime(rideDetails, effectiveDateTime),
			coupon,
			selection: effectiveCab,
		}),
		Type: getTripType(rideDetails),
		...(paymentType ? { 'Payment type': paymentType } : {}),
		...(includeFunnel ? { Funnel: 'Cab Reschedule' } : {}),
		...(parentBookingId ? { 'Parent Booking ID': parentBookingId } : {}),
		...(includeRequestTimestamp
			? { 'Reschedule Request Timestamp': formatDateTime(effectiveDateTime) }
			: {}),
		...(selectedCab && 'rescheduleDetails' in selectedCab
			? { 'Payment Amount': getReschedulePaymentAmount(selectedCab, burnOpted) }
			: {}),
	};
}

const BOOKING_STATUS_EVENT_MAP: Partial<Record<BookingStatus, string>> = {
	CONFIRMED: 'Cab Book Success',
	COMPLETED: 'Cab Book Success',
	FAILED: 'Cab Book Failed',
	PAYMENT_PENDING: 'Cab Payment Pending',
	PAYMENT_SUCCESS_BOOKING_PENDING: 'Cab Book Pending',
	// CANCELLED: undefined, // No event as per current list
};
export const tripTracker = createTracker({
	getCommonAttributes,
	trackMap: {
		rescheduleInitiated: () => ({
			eventName: 'Cab Reschedule Initiated',
			eventData: getRescheduleEventAttributes({
				includeRequestTimestamp: false,
				includeFunnel: false,
			}),
		}),
		rescheduleAmountReview: (payload: {
			selectedDateTime: string;
			selectedCab: RescheduleCabOption;
			burnOpted?: boolean;
		}) => ({
			eventName: 'Cab Reschedule Amount Review',
			eventData: getRescheduleEventAttributes({
				...payload,
				includeFunnel: false,
			}),
		}),
		initPayment: (payload: {
			selectedDateTime: string;
			selectedCab: RescheduleCabOption;
			burnOpted?: boolean;
		}) => ({
			eventName: 'Cab Payment Initiated',
			eventData: getRescheduleEventAttributes(payload),
		}),
		/**
		 * Fires on the confirmation page (not at gateway-redirect time) so the
		 * event only emits once the rescheduled booking has actually landed as
		 * CONFIRMED / COMPLETED. Skips otherwise (the rest of the funnel is
		 * covered by `book()` → `Cab Book *` with Funnel='Cab Reschedule').
		 */
		rescheduleSuccess: () => {
			const bookingData = getTripBookingState().bookingData;
			if (!bookingData?.bookingDetail.reschedule) return;
			const { bookingStatus } = bookingData.bookingDetail;
			if (bookingStatus !== 'CONFIRMED' && bookingStatus !== 'COMPLETED') {
				return;
			}
			return {
				eventName: 'Cab Reschedule Success',
				eventData: getRescheduleEventAttributes({
					includeRequestTimestamp: false,
				}),
			};
		},
		/**
		 * Fires the `Cab Reschedule Failed` event. Two call patterns:
		 *
		 * - With `payload` (modal-time): called from `use-reschedule-actions.ts`
		 *   when `confirmReschedule` API returns an error response. Captures
		 *   the most common reschedule drop-off (user never proceeds past the
		 *   modal).
		 *
		 * - Without `payload` (confirmation-page): called from `TripPage` when
		 *   a rescheduled booking lands with `bookingStatus === 'FAILED'`
		 *   (e.g. payment succeeded but booking creation failed). State-driven;
		 *   reads bookingDetail from the trip-booking store.
		 *
		 * The two firing points are mutually exclusive in practice — if the
		 * modal API errors, the user never reaches the confirmation page;
		 * if it succeeds, the modal-time fire is skipped.
		 */
		rescheduleFailed: (payload?: {
			selectedDateTime: string;
			selectedCab: RescheduleCabOption;
			burnOpted?: boolean;
		}) => {
			if (payload) {
				return {
					eventName: 'Cab Reschedule Failed',
					eventData: getRescheduleEventAttributes(payload),
				};
			}
			const bookingData = getTripBookingState().bookingData;
			if (!bookingData?.bookingDetail.reschedule) return;
			if (bookingData.bookingDetail.bookingStatus !== 'FAILED') return;
			return {
				eventName: 'Cab Reschedule Failed',
				eventData: getRescheduleEventAttributes({
					includeRequestTimestamp: false,
				}),
			};
		},
		book: () => {
			const bookingData = getTripBookingState().bookingData;
			if (!bookingData) {
				return;
			}
			const {
				parentBookingId,
				bookingDetail: { bookingStatus, bookingId },
			} = bookingData;
			const eventName = BOOKING_STATUS_EVENT_MAP[bookingStatus];
			if (!eventName) {
				return;
			}
			const paymentType = getBookingPaymentType();
			// TODO: add payment amount & verify coupon data
			return {
				eventName,
				eventData: {
					'Booking ID': bookingId,
					Funnel: parentBookingId ? 'Cab Reschedule' : 'Cab',
					...(parentBookingId ? { 'Parent Booking ID': parentBookingId } : {}),
					...(paymentType ? { 'Payment type': paymentType } : {}),
				},
			};
		},
	},
});
